import { useCallback, useEffect, useMemo, useState } from 'react';
import StatusToast from './StatusToast';
import { adminApi } from './api';

const PAGE_SIZE = 30;

function SortIndicator({ active, direction }) {
  if (!active) return <span className="bo-sort-indicator">↕</span>;
  return (
    <span className="bo-sort-indicator bo-sort-indicator--active">
      {direction === 'asc' ? '↑' : '↓'}
    </span>
  );
}

function formatDate(value, withTime = false) {
  if (!value) return '—';
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) return '—';
  return withTime
    ? date.toLocaleString('fr-FR')
    : date.toLocaleDateString('fr-FR');
}

function hasPermission(permissions, permission) {
  return permissions?.includes('*') || permissions?.includes(permission);
}

function downloadJson(filename, data) {
  const blob = new Blob([JSON.stringify(data, null, 2)], {
    type: 'application/json',
  });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

function CandidateStatus({ status }) {
  const labels = {
    active: 'Actif',
    suspended: 'Suspendu',
    disabled: 'Désactivé',
    deletion_pending: 'Suppression en attente',
    deleted: 'Supprimé',
  };
  return (
    <span className={`bo-badge bo-badge--account-${status || 'active'}`}>
      {labels[status] || status || 'Actif'}
    </span>
  );
}

function DetailTabs({ tab, setTab }) {
  const tabs = [
    ['overview', 'Vue générale'],
    ['progress', 'Progression'],
    ['premium', 'Abonnement & accès'],
    ['forum', 'Forum'],
    ['security', 'Sécurité'],
    ['audit', 'Historique admin'],
  ];
  return (
    <div className="bo-tabs">
      {tabs.map(([key, label]) => (
        <button
          key={key}
          className={tab === key ? 'is-active' : ''}
          onClick={() => setTab(key)}
        >
          {label}
        </button>
      ))}
    </div>
  );
}

function ProfileEditor({ detail, onSaved, canManage }) {
  const candidate = detail.candidate;
  const [form, setForm] = useState({
    email: candidate.email || '',
    pseudonym: candidate.pseudonym || '',
    firstName: candidate.first_name || '',
    lastName: candidate.last_name || '',
    preferredLanguage: candidate.preferred_language || 'fr',
    emailNotifications: candidate.notification_preferences?.email !== false,
    preparationObjective: candidate.preparation_objective || '',
    targetedCompetitions: (candidate.targeted_competitions || []).join(', '),
    prioritySubjects: (candidate.priority_subjects || []).join(', '),
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  async function save(event) {
    event.preventDefault();
    if (!canManage) return;
    try {
      setBusy(true);
      setError('');
      await adminApi.updateUserProfile(candidate.id, {
        email: form.email,
        pseudonym: form.pseudonym,
        firstName: form.firstName,
        lastName: form.lastName,
        preferredLanguage: form.preferredLanguage,
        notificationPreferences: {
          ...(candidate.notification_preferences || {}),
          email: form.emailNotifications,
        },
        preparationObjective: form.preparationObjective || null,
        targetedCompetitions: form.targetedCompetitions
          .split(',')
          .map((item) => item.trim())
          .filter(Boolean),
        prioritySubjects: form.prioritySubjects
          .split(',')
          .map((item) => item.trim())
          .filter(Boolean),
      });
      await onSaved();
    } catch (saveError) {
      setError(saveError.message || 'Modification impossible.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="bo-form bo-profile-form" onSubmit={save}>
      <label>
        E-mail
        <input
          className="bo-input"
          type="email"
          value={form.email}
          disabled={!canManage}
          onChange={(event) =>
            setForm((previous) => ({ ...previous, email: event.target.value }))
          }
        />
      </label>
      <label>
        Pseudonyme
        <input
          className="bo-input"
          value={form.pseudonym}
          disabled={!canManage}
          onChange={(event) =>
            setForm((previous) => ({ ...previous, pseudonym: event.target.value }))
          }
        />
      </label>
      <label>
        Prénom
        <input
          className="bo-input"
          value={form.firstName}
          disabled={!canManage}
          onChange={(event) =>
            setForm((previous) => ({ ...previous, firstName: event.target.value }))
          }
        />
      </label>
      <label>
        Nom
        <input
          className="bo-input"
          value={form.lastName}
          disabled={!canManage}
          onChange={(event) =>
            setForm((previous) => ({ ...previous, lastName: event.target.value }))
          }
        />
      </label>
      <label>
        Langue
        <input
          className="bo-input"
          value={form.preferredLanguage}
          disabled={!canManage}
          onChange={(event) =>
            setForm((previous) => ({
              ...previous,
              preferredLanguage: event.target.value,
            }))
          }
        />
      </label>
      <label className="bo-checkbox">
        <input
          type="checkbox"
          checked={form.emailNotifications}
          disabled={!canManage}
          onChange={(event) =>
            setForm((previous) => ({
              ...previous,
              emailNotifications: event.target.checked,
            }))
          }
        />
        Notifications e-mail autorisées
      </label>
      <label>
        Concours ciblés
        <input
          className="bo-input"
          placeholder="ENAREF, Douanes, ENSP…"
          value={form.targetedCompetitions}
          disabled={!canManage}
          onChange={(event) =>
            setForm((previous) => ({
              ...previous,
              targetedCompetitions: event.target.value,
            }))
          }
        />
      </label>
      <label>
        Matières prioritaires
        <input
          className="bo-input"
          placeholder="Mathématiques, Français…"
          value={form.prioritySubjects}
          disabled={!canManage}
          onChange={(event) =>
            setForm((previous) => ({
              ...previous,
              prioritySubjects: event.target.value,
            }))
          }
        />
      </label>
      <label className="bo-span-2">
        Objectif de préparation
        <textarea
          className="bo-input"
          rows="3"
          value={form.preparationObjective}
          disabled={!canManage}
          onChange={(event) =>
            setForm((previous) => ({
              ...previous,
              preparationObjective: event.target.value,
            }))
          }
        />
      </label>
      {error && <div className="bo-alert bo-alert--error bo-span-2">{error}</div>}
      {canManage && (
        <button className="bo-button bo-button--primary" disabled={busy}>
          {busy ? 'Enregistrement…' : 'Enregistrer le profil'}
        </button>
      )}
    </form>
  );
}

export default function UserManagement({ permissions = [], onUnauthorized }) {
  const [filters, setFilters] = useState({
    search: '',
    status: '',
    tier: '',
    forumStatus: '',
  });
  const [result, setResult] = useState({ items: [], total: 0 });
  const [offset, setOffset] = useState(0);
  const [sort, setSort] = useState({
    key: 'last_activity',
    direction: 'desc',
  });
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [tab, setTab] = useState('overview');
  const [loadingList, setLoadingList] = useState(false);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  const canManage = hasPermission(permissions, 'users:manage');
  const canSecurity = hasPermission(permissions, 'users:security');
  const canExport = hasPermission(permissions, 'users:export');
  const canBilling = hasPermission(permissions, 'billing:manage');
  const canForum = hasPermission(permissions, 'forum:moderate');
  const canDelete = hasPermission(permissions, 'users:delete');

  const loadList = useCallback(async (requestedOffset = offset) => {
    try {
      setLoadingList(true);
      setError('');
      const next = await adminApi.users({
        ...filters,
        sort: sort.key,
        order: sort.direction,
        limit: PAGE_SIZE,
        offset: requestedOffset,
      });
      setResult(next || { items: [], total: 0 });

      // Selection is explicit: loading the catalogue must never automatically
      // open another candidate or discard the currently edited profile.
    } catch (loadError) {
      if (loadError.status === 401) onUnauthorized?.();
      else setError(loadError.message || 'Chargement des candidats impossible.');
    } finally {
      setLoadingList(false);
    }
  }, [filters, offset, onUnauthorized, sort]);

  const loadDetail = useCallback(async (id = selectedId) => {
    if (!id) return;
    try {
      setLoadingDetail(true);
      setError('');
      const next = await adminApi.userDetail(id);
      setDetail(next);
    } catch (loadError) {
      if (loadError.status === 401) onUnauthorized?.();
      else setError(loadError.message || 'Chargement du candidat impossible.');
    } finally {
      setLoadingDetail(false);
    }
  }, [onUnauthorized, selectedId]);

  useEffect(() => {
    loadList(offset);
  }, [offset, sort.key, sort.direction]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (selectedId) loadDetail(selectedId);
  }, [selectedId]); // eslint-disable-line react-hooks/exhaustive-deps

  function applyFilters() {
    if (offset === 0) {
      loadList(0);
    } else {
      setOffset(0);
    }
  }

  function moveToWorkspaceTop() {
    // Preserve the user's filters and catalogue page while avoiding a jump
    // into the middle of a newly opened detail view.
    if (document.scrollingElement) document.scrollingElement.scrollTop = 0;
  }

  function openCandidate(id) {
    if (Number(id) !== Number(selectedId)) setDetail(null);
    setSelectedId(id);
    setTab('overview');
    setNotice('');
    moveToWorkspaceTop();
  }

  function returnToCatalog() {
    setSelectedId(null);
    setDetail(null);
    setTab('overview');
    setNotice('');
    moveToWorkspaceTop();
  }

  function changeSort(key) {
    setSort((current) => ({
      key,
      direction:
        current.key === key && current.direction === 'asc' ? 'desc' : 'asc',
    }));
    setOffset(0);
  }

  async function action(work, message, { refreshList = true } = {}) {
    try {
      setError('');
      setNotice('');
      await work();
      setNotice(message);
      if (selectedId) await loadDetail(selectedId);
      if (refreshList) await loadList();
      return true;
    } catch (actionError) {
      if (actionError.status === 401) onUnauthorized?.();
      else setError(actionError.message || 'Action impossible.');
      return false;
    }
  }

  async function suspendAccount() {
    const reason = window.prompt('Motif de suspension :');
    if (!reason?.trim()) return;
    const until = window.prompt(
      'Date de fin facultative (ex. 2026-10-31T23:59) ; laisser vide pour une suspension indéfinie :'
    );
    await action(
      () =>
        adminApi.suspendUser(selectedId, {
          reason: reason.trim(),
          until: until?.trim() || null,
        }),
      'Compte suspendu.'
    );
  }

  async function disableAccount() {
    const reason = window.prompt('Motif de désactivation :');
    if (!reason?.trim()) return;
    if (!window.confirm('Désactiver ce compte candidat ?')) return;
    await action(
      () => adminApi.disableUser(selectedId, { reason: reason.trim() }),
      'Compte désactivé.'
    );
  }

  async function grantPremium() {
    const endsAt = window.prompt(
      'Date de fin Premium (ex. 2026-12-31T23:59) :'
    );
    if (!endsAt?.trim()) return;
    const reason = window.prompt('Motif de l’activation Premium :');
    if (!reason?.trim()) return;
    await action(
      () =>
        adminApi.grantUserPremium(selectedId, {
          planCode: 'premium_manual',
          endsAt: endsAt.trim(),
          reason: reason.trim(),
        }),
      'Premium activé.'
    );
  }

  async function extendPremium() {
    const endsAt = window.prompt('Nouvelle date de fin Premium :');
    if (!endsAt?.trim()) return;
    const reason = window.prompt('Motif de la prolongation :');
    if (!reason?.trim()) return;
    await action(
      () =>
        adminApi.extendUserPremium(selectedId, {
          endsAt: endsAt.trim(),
          reason: reason.trim(),
        }),
      'Premium prolongé.'
    );
  }

  async function revokePremium() {
    const reason = window.prompt('Motif de la révocation Premium :');
    if (!reason?.trim()) return;
    if (!window.confirm('Révoquer immédiatement l’accès Premium ?')) return;
    await action(
      () => adminApi.revokeUserPremium(selectedId, reason.trim()),
      'Premium révoqué.'
    );
  }

  async function exportData() {
    try {
      const data = await adminApi.exportUser(selectedId);
      downloadJson(
        `concoursbf-candidate-${selectedId}-export.json`,
        data
      );
      setNotice('Export généré.');
    } catch (exportError) {
      setError(exportError.message || 'Export impossible.');
    }
  }

  async function requestDeletion() {
    const reason = window.prompt('Motif de la demande de suppression :');
    if (!reason?.trim()) return;
    if (!window.confirm('Placer ce compte en attente de suppression ?')) return;
    await action(
      () => adminApi.requestUserDeletion(selectedId, reason.trim()),
      'Compte placé en attente de suppression.'
    );
  }

  async function anonymize() {
    const reason = window.prompt(
      'Motif de l’anonymisation définitive (le compte doit déjà être en attente de suppression) :'
    );
    if (!reason?.trim()) return;
    const confirmation = window.prompt(
      'Tapez ANONYMIZE pour confirmer définitivement :'
    );
    if (confirmation !== 'ANONYMIZE') return;
    await action(
      () => adminApi.anonymizeUser(selectedId, reason.trim()),
      'Compte anonymisé.'
    );
  }

  const candidate = detail?.candidate;
  const fullName = useMemo(
    () =>
      candidate
        ? [candidate.first_name, candidate.last_name].filter(Boolean).join(' ') ||
          candidate.pseudonym
        : '',
    [candidate]
  );

  return (
    <div className="bo-stack">
      <header>
        <p className="bo-eyebrow">Administration</p>
        <h2>Candidats</h2>
        <p className="bo-muted">
          Recherche, progression, accès Premium, sécurité, forum et cycle de vie
          des comptes du front-office.
        </p>
      </header>

      {error && <div className="bo-alert bo-alert--error">{error}</div>}
      <StatusToast message={notice} />

      {!selectedId && (
      <section className="bo-panel">
        <div className="bo-form bo-user-filters">
          <label>
            Recherche
            <input
              className="bo-input"
              placeholder="Nom, pseudonyme, e-mail…"
              value={filters.search}
              onChange={(event) =>
                setFilters((previous) => ({
                  ...previous,
                  search: event.target.value,
                }))
              }
            />
          </label>
          <label>
            État compte
            <select
              className="bo-input"
              value={filters.status}
              onChange={(event) =>
                setFilters((previous) => ({
                  ...previous,
                  status: event.target.value,
                }))
              }
            >
              <option value="">Tous</option>
              <option value="active">Actif</option>
              <option value="suspended">Suspendu</option>
              <option value="disabled">Désactivé</option>
              <option value="deletion_pending">Suppression en attente</option>
              <option value="deleted">Supprimé</option>
            </select>
          </label>
          <label>
            Accès
            <select
              className="bo-input"
              value={filters.tier}
              onChange={(event) =>
                setFilters((previous) => ({ ...previous, tier: event.target.value }))
              }
            >
              <option value="">Tous</option>
              <option value="free">Free</option>
              <option value="premium">Premium</option>
            </select>
          </label>
          <label>
            Forum
            <select
              className="bo-input"
              value={filters.forumStatus}
              onChange={(event) =>
                setFilters((previous) => ({
                  ...previous,
                  forumStatus: event.target.value,
                }))
              }
            >
              <option value="">Tous</option>
              <option value="active">Actif</option>
              <option value="suspended">Suspendu</option>
            </select>
          </label>
          <button className="bo-button bo-button--primary" onClick={applyFilters}>
            Rechercher
          </button>
        </div>
      </section>
      )}

      {selectedId && (
        <button type="button" className="bo-button bo-button--ghost bo-candidate-back"
          onClick={returnToCatalog}>Retour au catalogue des candidats</button>
      )}

      <div className={selectedId ? 'bo-user-workspace bo-user-workspace--detail' : 'bo-user-workspace'}>
        {!selectedId && (
        <section className="bo-panel bo-user-grid-panel">
          <div className="bo-panel__header bo-user-grid-header">
            <div>
              <h3>Base candidats</h3>
              <p className="bo-muted">
                {result.total || 0} candidat(s) · cliquez sur une ligne pour ouvrir
                la fiche 360°.
              </p>
            </div>
            <span className="bo-kpi">
              {loadingList ? 'Chargement…' : `${result.total || 0} résultat(s)`}
            </span>
          </div>

          {loadingList && !(result.items || []).length ? (
            <div className="bo-loading">Chargement…</div>
          ) : (result.items || []).length === 0 ? (
            <div className="bo-empty">Aucun candidat.</div>
          ) : (
            <div className="bo-candidate-grid-wrap">
              <table className="bo-table bo-candidate-grid">
                <thead>
                  <tr>
                    <th>
                      <button
                        className="bo-sort-button"
                        onClick={() => changeSort('name')}
                      >
                        Candidat
                        <SortIndicator
                          active={sort.key === 'name'}
                          direction={sort.direction}
                        />
                      </button>
                    </th>
                    <th>
                      <button
                        className="bo-sort-button"
                        onClick={() => changeSort('email')}
                      >
                        E-mail
                        <SortIndicator
                          active={sort.key === 'email'}
                          direction={sort.direction}
                        />
                      </button>
                    </th>
                    <th>
                      <button
                        className="bo-sort-button"
                        onClick={() => changeSort('status')}
                      >
                        Statut
                        <SortIndicator
                          active={sort.key === 'status'}
                          direction={sort.direction}
                        />
                      </button>
                    </th>
                    <th>
                      <button
                        className="bo-sort-button"
                        onClick={() => changeSort('access')}
                      >
                        Accès
                        <SortIndicator
                          active={sort.key === 'access'}
                          direction={sort.direction}
                        />
                      </button>
                    </th>
                    <th>E-mail vérifié</th>
                    <th>Forum</th>
                    <th>
                      <button
                        className="bo-sort-button"
                        onClick={() => changeSort('created_at')}
                      >
                        Inscription
                        <SortIndicator
                          active={sort.key === 'created_at'}
                          direction={sort.direction}
                        />
                      </button>
                    </th>
                    <th>
                      <button
                        className="bo-sort-button"
                        onClick={() => changeSort('last_activity')}
                      >
                        Dernière activité
                        <SortIndicator
                          active={sort.key === 'last_activity'}
                          direction={sort.direction}
                        />
                      </button>
                    </th>
                    <th>
                      <button
                        className="bo-sort-button"
                        onClick={() => changeSort('attempts')}
                      >
                        Tentatives
                        <SortIndicator
                          active={sort.key === 'attempts'}
                          direction={sort.direction}
                        />
                      </button>
                    </th>
                    <th>
                      <button
                        className="bo-sort-button"
                        onClick={() => changeSort('average_score')}
                      >
                        Moyenne
                        <SortIndicator
                          active={sort.key === 'average_score'}
                          direction={sort.direction}
                        />
                      </button>
                    </th>
                    <th aria-label="Actions">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {(result.items || []).map((user) => {
                    const selected =
                      Number(selectedId) === Number(user.id);
                    const displayName =
                      [user.first_name, user.last_name]
                        .filter(Boolean)
                        .join(' ') || user.pseudonym || user.email;
                    const average =
                      user.average_score === null ||
                      user.average_score === undefined
                        ? '—'
                        : `${Number(user.average_score).toFixed(1)}/20`;

                    return (
                      <tr
                        key={user.id}
                        className={selected ? 'is-selected' : ''}
                        tabIndex="0"
                        aria-selected={selected}
                        onClick={() => openCandidate(user.id)}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter' || event.key === ' ') {
                            event.preventDefault();
                            openCandidate(user.id);
                          }
                        }}
                      >
                        <td className="bo-candidate-grid__identity">
                          <strong>{displayName}</strong>
                          <small>@{user.pseudonym || '—'}</small>
                        </td>
                        <td>
                          <span className="bo-cell-ellipsis" title={user.email}>
                            {user.email}
                          </span>
                        </td>
                        <td>
                          <CandidateStatus status={user.account_status} />
                        </td>
                        <td>
                          <span
                            className={`bo-badge ${
                              user.premium_active
                                ? 'bo-badge--premium'
                                : 'bo-badge--free'
                            }`}
                          >
                            {user.premium_active ? 'Premium' : 'Free'}
                          </span>
                        </td>
                        <td>
                          <span
                            className={`bo-grid-state ${
                              user.email_verified_at
                                ? 'bo-grid-state--ok'
                                : 'bo-grid-state--muted'
                            }`}
                          >
                            {user.email_verified_at ? '✓ Oui' : 'Non'}
                          </span>
                        </td>
                        <td>
                          <span
                            className={`bo-grid-state ${
                              user.forum_status === 'active'
                                ? 'bo-grid-state--ok'
                                : 'bo-grid-state--warn'
                            }`}
                          >
                            {user.forum_status === 'active'
                              ? 'Actif'
                              : 'Suspendu'}
                          </span>
                        </td>
                        <td>{formatDate(user.created_at)}</td>
                        <td>{formatDate(user.last_activity_at, true)}</td>
                        <td className="bo-cell-number">
                          {Number(user.total_attempts || 0)}
                        </td>
                        <td className="bo-cell-number">{average}</td>
                        <td>
                          <button
                            className="bo-button bo-button--ghost bo-button--compact"
                            onClick={(event) => {
                              event.stopPropagation();
                              openCandidate(user.id);
                            }}
                          >
                            Ouvrir
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          <div className="bo-row bo-row--between bo-pagination">
            <small className="bo-muted">
              {result.total
                ? `${offset + 1}–${Math.min(
                    offset + (result.items || []).length,
                    result.total
                  )} sur ${result.total}`
                : '0 candidat'}
            </small>
            <div className="bo-actions">
              <button
                className="bo-button bo-button--ghost"
                disabled={loadingList || offset === 0}
                onClick={() =>
                  setOffset((current) => Math.max(0, current - PAGE_SIZE))
                }
              >
                Précédent
              </button>
              <button
                className="bo-button bo-button--ghost"
                disabled={
                  loadingList ||
                  offset + (result.items || []).length >= (result.total || 0)
                }
                onClick={() => setOffset((current) => current + PAGE_SIZE)}
              >
                Suivant
              </button>
            </div>
          </div>
        </section>
        )}

        {selectedId && (
        <section className="bo-stack bo-user-detail">
          {loadingDetail && !detail ? (
            <div className="bo-panel bo-loading">Chargement du profil…</div>
          ) : candidate ? (
            <>
              <section className="bo-panel bo-candidate-summary">
                <div className="bo-panel__header">
                  <div>
                    <p className="bo-eyebrow">Candidat #{candidate.id}</p>
                    <h3>{fullName}</h3>
                    <p className="bo-muted">
                      {candidate.email} · @{candidate.pseudonym}
                    </p>
                  </div>
                  <div className="bo-actions">
                    <CandidateStatus status={candidate.account_status} />
                    <span
                      className={`bo-badge ${
                        detail.access?.premiumActive
                          ? 'bo-badge--premium'
                          : 'bo-badge--free'
                      }`}
                    >
                      {detail.access?.premiumActive ? 'Premium' : 'Free'}
                    </span>
                  </div>
                </div>
                <DetailTabs tab={tab} setTab={setTab} />
              </section>

              {tab === 'overview' && (
                <>
                  <div className="bo-stats-grid bo-candidate-overview-stats">
                    <article className="bo-stat-card">
                      <strong>{detail.progress?.totalAttempts || 0}</strong>
                      <span>Tentatives</span>
                    </article>
                    <article className="bo-stat-card">
                      <strong>
                        {(detail.progress?.averageScore || 0).toFixed(1)}/20
                      </strong>
                      <span>Moyenne</span>
                    </article>
                    <article className="bo-stat-card">
                      <strong>{detail.security?.activeSessionCount || 0}</strong>
                      <span>Sessions actives</span>
                    </article>
                    <article className="bo-stat-card">
                      <strong>{detail.learning?.difficultQuestions?.length || 0}</strong>
                      <span>Questions difficiles</span>
                    </article>
                  </div>
                  <section className="bo-panel bo-candidate-profile">
                    <div className="bo-candidate-section-heading">
                      <h3>Profil</h3>
                      <p className="bo-muted">Coordonnées, préférences et objectifs de préparation.</p>
                    </div>
                    <ProfileEditor
                      detail={detail}
                      canManage={canManage}
                      onSaved={() => loadDetail(selectedId)}
                    />
                    <details className="bo-candidate-meta-details">
                      <summary>Informations administratives et consentements</summary>
                      <div className="bo-meta-grid">
                      <div>
                        <span>Inscription</span>
                        <strong>{formatDate(candidate.created_at, true)}</strong>
                      </div>
                      <div>
                        <span>Dernière connexion</span>
                        <strong>{formatDate(candidate.last_login_at, true)}</strong>
                      </div>
                      <div>
                        <span>Dernière activité</span>
                        <strong>{formatDate(candidate.last_activity_at, true)}</strong>
                      </div>
                      <div>
                        <span>E-mail vérifié</span>
                        <strong>
                          {candidate.email_verified_at
                            ? formatDate(candidate.email_verified_at, true)
                            : 'Non'}
                        </strong>
                      </div>
                      <div>
                        <span>Notifications e-mail</span>
                        <strong>
                          {candidate.notification_preferences?.email === false
                            ? 'Désactivées'
                            : 'Activées'}
                        </strong>
                      </div>
                      <div>
                        <span>Conditions d’utilisation</span>
                        <strong>
                          {candidate.terms_accepted_at
                            ? `${formatDate(candidate.terms_accepted_at)} · ${candidate.terms_version || 'version inconnue'}`
                            : 'Non enregistré'}
                        </strong>
                      </div>
                      <div>
                        <span>Confidentialité</span>
                        <strong>
                          {candidate.privacy_accepted_at
                            ? `${formatDate(candidate.privacy_accepted_at)} · ${candidate.privacy_version || 'version inconnue'}`
                            : 'Non enregistré'}
                        </strong>
                      </div>
                      </div>
                    </details>
                  </section>

                  {canManage && (
                    <section className="bo-panel">
                      <h3>Cycle de vie du compte</h3>
                      <div className="bo-actions">
                        <button
                          className="bo-button bo-button--secondary"
                          onClick={suspendAccount}
                        >
                          Suspendre
                        </button>
                        <button
                          className="bo-button bo-button--secondary"
                          onClick={() =>
                            action(
                              () => adminApi.reactivateUser(selectedId),
                              'Compte réactivé.'
                            )
                          }
                        >
                          Réactiver
                        </button>
                        <button
                          className="bo-button bo-button--danger-ghost"
                          onClick={disableAccount}
                        >
                          Désactiver
                        </button>
                        <button
                          className="bo-button bo-button--danger-ghost"
                          onClick={requestDeletion}
                        >
                          Demander suppression
                        </button>
                        {canDelete && (
                          <button
                            className="bo-button bo-button--danger-ghost"
                            onClick={anonymize}
                          >
                            Anonymiser définitivement
                          </button>
                        )}
                      </div>
                    </section>
                  )}
                </>
              )}

              {tab === 'progress' && (
                <>
                  <div className="bo-stats-grid">
                    <article className="bo-stat-card">
                      <strong>{detail.progress?.totalQuizAttempts || 0}</strong>
                      <span>Quiz</span>
                    </article>
                    <article className="bo-stat-card">
                      <strong>
                        {(detail.progress?.quizAvgScore || 0).toFixed(1)}/20
                      </strong>
                      <span>Moyenne Quiz</span>
                    </article>
                    <article className="bo-stat-card">
                      <strong>{detail.progress?.totalSimulationAttempts || 0}</strong>
                      <span>Simulations</span>
                    </article>
                    <article className="bo-stat-card">
                      <strong>
                        {(detail.progress?.simAvgScore || 0).toFixed(1)}/20
                      </strong>
                      <span>Moyenne simulations</span>
                    </article>
                  </div>
                  <section className="bo-panel">
                    <h3>Activité récente</h3>
                    <div className="bo-table-wrap">
                      <table className="bo-table">
                        <thead>
                          <tr>
                            <th>Type</th>
                            <th>Activité</th>
                            <th>Score</th>
                            <th>Date</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(detail.recentActivities || []).map((activity, index) => (
                            <tr key={activity.id || `${activity.activity_type}-${index}`}>
                              <td>{activity.activity_type}</td>
                              <td>{activity.title || activity.subject || '—'}</td>
                              <td>
                                {activity.score !== null && activity.score !== undefined
                                  ? `${activity.score}/20`
                                  : '—'}
                              </td>
                              <td>{formatDate(activity.occurred_at, true)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </section>
                  <section className="bo-panel">
                    <h3>Questions difficiles</h3>
                    {(detail.learning?.difficultQuestions || []).length ? (
                      <div className="bo-list">
                        {detail.learning.difficultQuestions.map((question) => (
                          <div className="bo-question-card" key={question.question_id}>
                            <strong>{question.question_text}</strong>
                            <small>
                              {question.module_title || question.module_slug || '—'} ·
                              erreurs : {question.times_wrong || 0} / vues :{' '}
                              {question.times_seen || 0}
                            </small>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="bo-empty">Aucune question marquée difficile.</div>
                    )}
                  </section>
                  <section className="bo-panel">
                    <div className="bo-panel__header">
                      <div>
                        <h3>Ressources</h3>
                        <p className="bo-muted">
                          {detail.learning?.readResourcesCount || 0} ressource(s) lue(s)
                        </p>
                      </div>
                      <span className="bo-kpi">
                        {detail.learning?.favoriteResources?.length || 0} favori(s)
                      </span>
                    </div>
                    {(detail.learning?.favoriteResources || []).length ? (
                      <div className="bo-list">
                        {detail.learning.favoriteResources.map((resource) => (
                          <div className="bo-question-card" key={resource.resource_id}>
                            <strong>{resource.resource_title || 'Ressource'}</strong>
                            <small>
                              {resource.access_tier === 'premium' ? 'Premium' : 'Free'}
                              {resource.is_read ? ' · lue' : ''}
                            </small>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="bo-empty">Aucune ressource favorite.</div>
                    )}
                  </section>
                </>
              )}

              {tab === 'premium' && (
                <>
                  <section className="bo-panel">
                    <div className="bo-panel__header">
                      <div>
                        <h3>Accès actuel</h3>
                        <p className="bo-muted">
                          {detail.access?.premiumActive
                            ? `Premium jusqu’au ${formatDate(
                                detail.access?.premiumExpiresAt,
                                true
                              )}`
                            : 'Accès Free'}
                        </p>
                      </div>
                      {canBilling && (
                        <div className="bo-actions">
                          {!detail.access?.premiumActive && (
                            <button
                              className="bo-button bo-button--primary"
                              onClick={grantPremium}
                            >
                              Donner Premium
                            </button>
                          )}
                          {detail.access?.premiumActive && (
                            <>
                              <button
                                className="bo-button bo-button--secondary"
                                onClick={extendPremium}
                              >
                                Prolonger
                              </button>
                              <button
                                className="bo-button bo-button--danger-ghost"
                                onClick={revokePremium}
                              >
                                Révoquer
                              </button>
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  </section>
                  <section className="bo-panel">
                    <h3>Historique des abonnements</h3>
                    <div className="bo-table-wrap">
                      <table className="bo-table">
                        <thead>
                          <tr>
                            <th>Plan</th>
                            <th>Statut</th>
                            <th>Début</th>
                            <th>Fin</th>
                            <th>Source</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(detail.subscriptions || []).map((subscription) => (
                            <tr key={subscription.id}>
                              <td>{subscription.plan_code}</td>
                              <td>{subscription.status}</td>
                              <td>{formatDate(subscription.starts_at, true)}</td>
                              <td>{formatDate(subscription.ends_at, true)}</td>
                              <td>{subscription.source}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </section>
                </>
              )}

              {tab === 'forum' && (
                <>
                  <section className="bo-panel">
                    <div className="bo-panel__header">
                      <div>
                        <h3>État Forum</h3>
                        <p className="bo-muted">
                          {candidate.forum_status === 'suspended'
                            ? `Suspendu${candidate.forum_suspended_until ? ` jusqu’au ${formatDate(candidate.forum_suspended_until, true)}` : ''}`
                            : 'Participation autorisée'}
                        </p>
                      </div>
                      {canForum && (
                        <div className="bo-actions">
                          {candidate.forum_status === 'active' ? (
                            <button
                              className="bo-button bo-button--danger-ghost"
                              onClick={async () => {
                                const reason = window.prompt(
                                  'Motif de suspension du forum :'
                                );
                                if (!reason?.trim()) return;
                                const until = window.prompt(
                                  'Fin facultative (date/heure) :'
                                );
                                await action(
                                  () =>
                                    adminApi.suspendUserForum(selectedId, {
                                      reason: reason.trim(),
                                      until: until?.trim() || null,
                                    }),
                                  'Participation au forum suspendue.'
                                );
                              }}
                            >
                              Suspendre du forum
                            </button>
                          ) : (
                            <button
                              className="bo-button bo-button--secondary"
                              onClick={() =>
                                action(
                                  () => adminApi.reactivateUserForum(selectedId),
                                  'Participation au forum réactivée.'
                                )
                              }
                            >
                              Réactiver le forum
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  </section>
                  <section className="bo-panel">
                    <h3>
                      Sujets ({detail.forum?.summary?.topics || 0}) · Réponses (
                      {detail.forum?.summary?.responses || 0})
                    </h3>
                    <div className="bo-list">
                      {(detail.forum?.topics || []).map((topic) => (
                        <div className="bo-row bo-row--between bo-question-card" key={topic.id}>
                          <div>
                            <strong>{topic.title}</strong>
                            <small>
                              {topic.category} · {formatDate(topic.created_at, true)}
                            </small>
                          </div>
                          {canForum && (
                            <button
                              className="bo-button bo-button--danger-ghost"
                              onClick={() => {
                                if (!window.confirm('Supprimer ce sujet du forum ?')) return;
                                action(
                                  () =>
                                    adminApi.deleteUserForumTopic(
                                      selectedId,
                                      topic.id
                                    ),
                                  'Sujet supprimé.'
                                );
                              }}
                            >
                              Supprimer
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  </section>
                  <section className="bo-panel">
                    <h3>Réponses récentes</h3>
                    {(detail.forum?.responses || []).length ? (
                      <div className="bo-list">
                        {detail.forum.responses.map((response) => (
                          <div
                            className="bo-row bo-row--between bo-question-card"
                            key={response.id}
                          >
                            <div>
                              <strong>{response.topic_title || 'Sujet supprimé'}</strong>
                              <small>
                                {response.content?.slice(0, 220) || '—'}
                                {response.content?.length > 220 ? '…' : ''}
                                {' · '}
                                {formatDate(response.created_at, true)}
                              </small>
                            </div>
                            {canForum && (
                              <button
                                className="bo-button bo-button--danger-ghost"
                                onClick={() => {
                                  if (!window.confirm('Supprimer cette réponse du forum ?')) return;
                                  action(
                                    () =>
                                      adminApi.deleteUserForumResponse(
                                        selectedId,
                                        response.id
                                      ),
                                    'Réponse supprimée.'
                                  );
                                }}
                              >
                                Supprimer
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="bo-empty">Aucune réponse récente.</div>
                    )}
                  </section>
                </>
              )}

              {tab === 'security' && (
                <>
                  <section className="bo-panel">
                    <h3>Sécurité du compte</h3>
                    <div className="bo-meta-grid">
                      <div>
                        <span>E-mail vérifié</span>
                        <strong>{candidate.email_verified_at ? 'Oui' : 'Non'}</strong>
                      </div>
                      <div>
                        <span>Sessions actives</span>
                        <strong>{detail.security?.activeSessionCount || 0}</strong>
                      </div>
                      <div>
                        <span>Dernière connexion</span>
                        <strong>{formatDate(candidate.last_login_at, true)}</strong>
                      </div>
                      <div>
                        <span>Version sessions</span>
                        <strong>{candidate.session_version}</strong>
                      </div>
                      <div>
                        <span>Échecs de connexion</span>
                        <strong>{candidate.failed_login_attempts || 0}</strong>
                      </div>
                      <div>
                        <span>Verrouillage connexion</span>
                        <strong>
                          {candidate.locked_until
                            ? formatDate(candidate.locked_until, true)
                            : 'Non'}
                        </strong>
                      </div>
                    </div>
                    {canSecurity && (
                      <div className="bo-actions bo-actions--spaced">
                        {candidate.locked_until && (
                          <button
                            className="bo-button bo-button--secondary"
                            onClick={() =>
                              action(
                                () => adminApi.unlockUserLogin(selectedId),
                                'Verrouillage de connexion levé.'
                              )
                            }
                          >
                            Déverrouiller la connexion
                          </button>
                        )}
                        <button
                          className="bo-button bo-button--secondary"
                          onClick={async () => {
                            const reason =
                              window.prompt('Motif de la déconnexion forcée :') ||
                              'Action administrateur';
                            await action(
                              () =>
                                adminApi.forceLogoutUser(
                                  selectedId,
                                  reason.trim()
                                ),
                              'Toutes les sessions ont été révoquées.'
                            );
                          }}
                        >
                          Déconnecter toutes les sessions
                        </button>
                        <button
                          className="bo-button bo-button--secondary"
                          onClick={() =>
                            action(
                              () => adminApi.sendUserPasswordReset(selectedId),
                              'E-mail de réinitialisation envoyé.',
                              { refreshList: false }
                            )
                          }
                        >
                          Envoyer réinitialisation mot de passe
                        </button>
                        {!candidate.email_verified_at && (
                          <button
                            className="bo-button bo-button--secondary"
                            onClick={() =>
                              action(
                                () =>
                                  adminApi.sendUserEmailVerification(selectedId),
                                'E-mail de vérification envoyé.',
                                { refreshList: false }
                              )
                            }
                          >
                            Renvoyer vérification e-mail
                          </button>
                        )}
                      </div>
                    )}
                  </section>
                  <section className="bo-panel">
                    <h3>Sessions actives</h3>
                    {(detail.security?.activeSessions || []).length ? (
                      <div className="bo-table-wrap">
                        <table className="bo-table">
                          <thead>
                            <tr>
                              <th>Appareil / navigateur</th>
                              <th>Créée</th>
                              <th>Dernière activité</th>
                              <th>Expiration</th>
                            </tr>
                          </thead>
                          <tbody>
                            {detail.security.activeSessions.map((session) => (
                              <tr key={session.id}>
                                <td>{session.user_agent || 'Non renseigné'}</td>
                                <td>{formatDate(session.created_at, true)}</td>
                                <td>{formatDate(session.last_seen_at, true)}</td>
                                <td>{formatDate(session.expires_at, true)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <div className="bo-empty">Aucune session active connue.</div>
                    )}
                    {canExport && (
                      <button
                        className="bo-button bo-button--ghost"
                        onClick={exportData}
                      >
                        Exporter les données du candidat
                      </button>
                    )}
                  </section>
                </>
              )}

              {tab === 'audit' && (
                <section className="bo-panel">
                  <h3>Historique des actions administratives</h3>
                  <div className="bo-table-wrap">
                    <table className="bo-table">
                      <thead>
                        <tr>
                          <th>Action</th>
                          <th>Administrateur</th>
                          <th>Date</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(detail.audit || []).map((entry) => (
                          <tr key={entry.id}>
                            <td>{entry.action}</td>
                            <td>
                              {entry.actor_pseudonym || entry.actor_email || 'Système'}
                            </td>
                            <td>{formatDate(entry.created_at, true)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </section>
              )}
            </>
          ) : null}
        </section>
        )}
      </div>
    </div>
  );
}
